import React from 'react';
import {
  Button,
  MenuToggle,
  MenuToggleElement,
  Select,
  SelectList,
  SelectOption,
} from '@patternfly/react-core';
import { AngleDoubleLeftIcon, AngleLeftIcon, AngleRightIcon } from '@patternfly/react-icons';
import './CursorPagination.css';

interface CursorPaginationProps {
  pageStartIndex: number;
  itemsOnPage: number;
  totalSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onFirstPage: () => void;
  perPage: number;
  onPerPageChange: (perPage: number) => void;
  perPageOptions?: number[];
  isDisabled?: boolean;
  isCompact?: boolean;
}

export const CursorPagination: React.FC<CursorPaginationProps> = ({
  pageStartIndex,
  itemsOnPage,
  totalSize,
  hasNextPage,
  hasPreviousPage,
  onNextPage,
  onPreviousPage,
  onFirstPage,
  perPage,
  onPerPageChange,
  perPageOptions = [10, 20, 50],
  isDisabled = false,
}) => {
  const [isPerPageOpen, setIsPerPageOpen] = React.useState(false);
  const displayStart = itemsOnPage > 0 ? pageStartIndex : 0;
  const endIndex = itemsOnPage > 0 ? pageStartIndex + itemsOnPage - 1 : 0;

  return (
    <div className="cursor-pagination">
      <div className="cursor-pagination__per-page">
        <Select
          role="menu"
          isOpen={isPerPageOpen}
          onOpenChange={setIsPerPageOpen}
          onSelect={(_e, value) => {
            onPerPageChange(Number(value));
            setIsPerPageOpen(false);
          }}
          selected={perPage}
          toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
            <MenuToggle
              ref={toggleRef}
              onClick={() => setIsPerPageOpen(!isPerPageOpen)}
              isExpanded={isPerPageOpen}
              isDisabled={isDisabled}
              variant="plainText"
              className="cursor-pagination__per-page-toggle"
            >
              {perPage} per page
            </MenuToggle>
          )}
        >
          <SelectList>
            {perPageOptions.map((opt) => (
              <SelectOption key={opt} value={opt}>{opt} per page</SelectOption>
            ))}
          </SelectList>
        </Select>
      </div>

      <span className="cursor-pagination__info">
        <strong>{displayStart} - {endIndex}</strong> of <strong>{totalSize}</strong>
      </span>

      <div className="cursor-pagination__nav">
        <Button
          variant="plain"
          icon={<AngleDoubleLeftIcon />}
          onClick={onFirstPage}
          isDisabled={isDisabled || !hasPreviousPage}
          aria-label="First page"
          size="sm"
        />
        <Button
          variant="plain"
          icon={<AngleLeftIcon />}
          onClick={onPreviousPage}
          isDisabled={isDisabled || !hasPreviousPage}
          aria-label="Previous page"
          size="sm"
        />
        <Button
          variant="plain"
          icon={<AngleRightIcon />}
          onClick={onNextPage}
          isDisabled={isDisabled || !hasNextPage}
          aria-label="Next page"
          size="sm"
        />
      </div>
    </div>
  );
};
